import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { format } from "date-fns";
import { ArrowPathIcon, ChevronLeftIcon, MinusIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { fetchWorkingSetPlan, fetchWorkingSetRead } from "@api";
import { buildApiQuery, cn } from "@utils";
import { markdownComponents } from "@areas/chatbot/utils";
import PlanOfAction, { PlanSkeleton } from "@components/suggestions/PlanOfAction";
import AiSparkleIcon from "../../../portal-template/components/SmartNoteModal/AiSparkleIcon";
import { splitIntoSections } from "../../../portal-template/components/SmartNoteModalV2/ReadBlock";
import { completeFilters, describeCondition } from "../../tasks/hooks/workingSetBuilder";
import { useBuilderFields } from "../../tasks/hooks/useBuilderFields";

const COLLAPSED_KEY = "workingSetRead.collapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
};

// Whether the panel is tucked away, remembered across visits. Owned by the page so the header chip and
// the panel's own minimise share it.
export const useWorkingSetReadCollapsed = () => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = useCallback(() => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSED_KEY, String(!c));
      } catch {
        // Ignore storage failures; the toggle still applies for this visit.
      }
      return !c;
    });
  }, []);
  return [collapsed, toggle];
};

const SLIDE_FRAMES = [
  { transform: "translateX(calc(100% + 2rem))", opacity: 0 },
  { transform: "none", opacity: 1 },
];

const prefersReducedMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Keeps the panel mounted while it slides out to the right, and slides it back in when reopened (not when
// it's simply there on load). Says whether to render it at all; reduced motion just shows and hides.
const useSlide = (panelRef, collapsed) => {
  const [rendered, setRendered] = useState(!collapsed);

  useLayoutEffect(() => {
    if (!collapsed) {
      setRendered(true);
      return undefined;
    }
    const panel = panelRef.current;
    if (!panel || prefersReducedMotion() || !panel.animate) {
      setRendered(false);
      return undefined;
    }
    const animation = panel.animate(SLIDE_FRAMES, {
      duration: 260,
      easing: "cubic-bezier(0.4, 0, 1, 1)",
      direction: "reverse",
      fill: "forwards",
    });
    animation.onfinish = () => setRendered(false);
    return () => animation.cancel();
  }, [collapsed, panelRef]);

  const slidBefore = useRef(rendered);
  useLayoutEffect(() => {
    if (!rendered) {
      slidBefore.current = false;
      return;
    }
    if (slidBefore.current || collapsed) return;
    slidBefore.current = true;
    const panel = panelRef.current;
    if (!panel || prefersReducedMotion() || !panel.animate) return;
    panel.animate(SLIDE_FRAMES, { duration: 320, easing: "cubic-bezier(0, 0, 0.2, 1)" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rendered]);

  return rendered;
};

// The page header's way back to the panel, after the notifications bell and styled like it; pressed while
// the panel is open.
export const WorkingSetReadChip = ({ open, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={open}
    aria-label="Plan of action"
    title="Plan of action"
    className={cn(
      "flex size-9 items-center justify-center rounded-lg transition-colors",
      open ? "bg-primary-100" : "hover:bg-zinc-100",
    )}
  >
    <AiSparkleIcon className="size-5" />
  </button>
);

// The plan of action and Ontik read for the working set open on Trade Accounts, floating over the table's
// corner. The first open of the day generates them and intelligence-service keeps them for the day, so
// later opens are instant until refreshed; nothing is fetched while the panel is closed. The read's first
// section leads the panel above the plan; Full read slides the whole read (the same emoji-led sections as
// an account's read) out to the panel's left, or opens it in place where there's no room beside it.
const WorkingSetRead = ({ set, accountCount, collapsed, onToggle, onHoverStep }) => {
  const fields = useBuilderFields();

  const query = useMemo(() => buildApiQuery({ filters: set.filters }), [set.filters]);
  const definition = useMemo(
    () => completeFilters(set.filters).map((f) => describeCondition(f, fields)),
    [set.filters, fields],
  );

  // Refresh asks the service to regenerate; any other fetch takes the day's kept read.
  const forceRefresh = useRef(false);
  const [flowOpen, setFlowOpen] = useState(false);
  const [readOpen, setReadOpen] = useState(false);
  const panelRef = useRef(null);
  const rendered = useSlide(panelRef, collapsed);

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ["workingSetRead", set.id, query],
    queryFn: ({ signal }) => {
      const refresh = forceRefresh.current;
      forceRefresh.current = false;
      return fetchWorkingSetRead({ name: set.name, query, definition, cohortId: set.id, refresh }, { signal });
    },
    enabled: !collapsed,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  // The plan comes from the accounts' stored suggestions, so it's cheap to load fresh; Regenerate asks the
  // service to refresh accounts that moved since their suggestion was made first.
  const regenerateNext = useRef(false);
  const [regenerating, setRegenerating] = useState(false);
  const planQuery = useQuery({
    queryKey: ["workingSetPlan", set.id, query],
    queryFn: async ({ signal }) => {
      const regenerateChanged = regenerateNext.current;
      regenerateNext.current = false;
      setRegenerating(regenerateChanged);
      try {
        return await fetchWorkingSetPlan({ query, regenerateChanged }, { signal });
      } finally {
        setRegenerating(false);
      }
    },
    enabled: !collapsed,
    staleTime: 60_000,
    gcTime: 0,
    retry: false,
  });
  const regenerate = () => {
    regenerateNext.current = true;
    planQuery.refetch();
  };

  const sections = data?.summary && !isFetching ? splitIntoSections(data.summary) : [];
  const [lead, ...rest] = sections;

  const subtitle = [
    accountCount != null && `${accountCount.toLocaleString()} account${accountCount === 1 ? "" : "s"}`,
    isFetching ? "Reading…" : data?.generatedAt && `Updated ${format(new Date(data.generatedAt), "HH:mm")}`,
  ]
    .filter(Boolean)
    .join(" · ");

  if (!rendered) return null;

  const readSections = (items, offset = 0) =>
    items.map((section, i) => (
      <div key={offset + i} className="animate-readIn" style={{ animationDelay: `${i * 180}ms` }}>
        <ReactMarkdown components={markdownComponents}>{section}</ReactMarkdown>
      </div>
    ));

  return (
    <div
      ref={panelRef}
      className={cn(
        "fixed bottom-4 right-4 flex max-w-[calc(100vw-2rem)] items-end gap-3",
        // Above the Ontik Intelligence button, but behind a suggestion's flow once one opens.
        flowOpen ? "z-30" : "z-[41]",
      )}
    >
      {readOpen && rest.length > 0 && (
        <aside
          aria-label="Ontik read"
          className="hidden max-h-[min(36rem,calc(100vh-8rem))] w-[26rem] flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl xl:flex"
        >
          <header className="flex flex-none items-center gap-2 border-b border-zinc-100 px-5 py-3.5">
            <AiSparkleIcon className="size-4" />
            <h3 className="text-sm font-semibold text-zinc-900">Ontik read</h3>
            <button
              type="button"
              onClick={() => setReadOpen(false)}
              aria-label="Close full read"
              className="ml-auto rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            >
              <XMarkIcon aria-hidden="true" className="size-4" />
            </button>
          </header>
          <div
            key={data?.generatedAt}
            className="min-h-0 space-y-3 overflow-y-auto px-5 py-5 text-sm leading-relaxed text-zinc-700 [&_strong]:font-semibold [&_strong]:text-zinc-900"
          >
            {readSections(sections)}
          </div>
        </aside>
      )}

      <section
        aria-label="Working set plan of action"
        className="flex max-h-[min(36rem,calc(100vh-8rem))] w-[28rem] max-w-full flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl"
      >
        <header className="flex flex-none items-center gap-3 border-b border-zinc-100 px-5 py-3.5">
          <span className="flex size-8 flex-none items-center justify-center rounded-lg bg-violet-50">
            <AiSparkleIcon className="size-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-sm font-semibold text-zinc-900">{set.name}</h2>
            {subtitle && <p className="truncate text-xs tabular-nums text-zinc-500">{subtitle}</p>}
          </div>
          <div className="flex flex-none items-center gap-0.5">
            <button
              type="button"
              onClick={() => {
                forceRefresh.current = true;
                refetch();
                regenerate();
              }}
              disabled={isFetching || planQuery.isFetching}
              aria-label="Refresh plan and read"
              title="Refresh"
              className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 disabled:cursor-default disabled:hover:bg-transparent"
            >
              <ArrowPathIcon
                aria-hidden="true"
                className={cn("size-4", (isFetching || planQuery.isFetching) && "animate-spin")}
              />
            </button>
            <button
              type="button"
              onClick={onToggle}
              aria-label="Minimise"
              title="Minimise"
              className="rounded-md p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
            >
              <MinusIcon aria-hidden="true" className="size-4" />
            </button>
          </div>
        </header>

        <div className="min-h-0 space-y-6 overflow-y-auto px-5 py-5" aria-live="polite">
          {isFetching ? (
            <div className="space-y-2.5 rounded-xl bg-violet-50/60 p-4">
              {["w-11/12", "w-full", "w-9/12"].map((width) => (
                <div key={width} className={`h-3 ${width} animate-pulse rounded bg-violet-100`} />
              ))}
            </div>
          ) : isError ? (
            <p className="text-sm text-zinc-500">
              Couldn't read this working set right now.{" "}
              <button type="button" onClick={() => refetch()} className="font-medium text-zinc-800 underline">
                Try again
              </button>
            </p>
          ) : (
            lead && (
              <section
                aria-label="Ontik read summary"
                className="rounded-xl bg-violet-50/60 p-4 ring-1 ring-inset ring-violet-100"
              >
                <div
                  key={data?.generatedAt}
                  className="space-y-2 text-sm leading-relaxed text-zinc-700 [&_strong]:font-semibold [&_strong]:text-zinc-900"
                >
                  {readSections([lead])}
                  {readOpen && <div className="space-y-2 xl:hidden">{readSections(rest, 1)}</div>}
                </div>
                {rest.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setReadOpen((o) => !o)}
                    aria-expanded={readOpen}
                    className="mt-3 flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900"
                  >
                    <ChevronLeftIcon aria-hidden="true" className="hidden size-3.5 xl:block" />
                    {readOpen ? "Hide full read" : "Full read"}
                  </button>
                )}
              </section>
            )
          )}
          {planQuery.isFetching ? (
            <div>
              {regenerating && (
                <p className="mb-2.5 text-xs text-zinc-500">
                  Regenerating the accounts that changed — this can take a minute…
                </p>
              )}
              <PlanSkeleton />
            </div>
          ) : planQuery.isError ? (
            <p className="text-sm text-zinc-500">
              Couldn't load the plan of action right now.{" "}
              <button type="button" onClick={() => planQuery.refetch()} className="font-medium text-zinc-800 underline">
                Try again
              </button>
            </p>
          ) : (
            <PlanOfAction plan={planQuery.data} onHoverStep={onHoverStep} onFlowChange={setFlowOpen} />
          )}
        </div>
      </section>
    </div>
  );
};

export default WorkingSetRead;
